/**
 * registerAgentTool — the in-house client-side frontend-tool registry
 * (the dependency-free `useCopilotAction` equivalent).
 *
 * WHY (Design note I, docs/plans/2026-05-15_(completed)_AGUI_BRIDGE_AND_HEADLESS_COPILOTKIT.md):
 * the only remaining value of headless CopilotKit is a ~50-line tool
 * registry built against *our* state model. We own a clean AG-UI
 * consumer (agui-to-stream.ts); the registry is the small missing
 * ergonomic, not a reason to take a +1 dep that tracks spec churn and
 * has a worse default (client-declared tools auto-advertise → breaks
 * our skill-gate + the Phase 3 security boundary).
 *
 * ── HARD RULE — advertisement authority (= Invariant 2 / Phase 3) ──
 * This registry registers **render + handler ONLY**. It does NOT declare
 * a tool to the model. The model-facing declaration (name, description,
 * JSON schema, requiredSkills) stays in the WORKER tool file; the worker
 * is the sole authority for tool advertisement and the skill-gate
 * (prepareStep → Tool.filterActive, processor.ts). The registry never
 * picks the channel either — the per-tool `transport` (and `handler`'s
 * presence) does. That one shape covers all three flavors below without
 * forcing a single execution model — the proof the abstraction is right.
 *
 * Three flavors — one registration shape:
 *
 * | flavor          | transport         | resolution channel                          |
 * |-----------------|-------------------|---------------------------------------------|
 * | render-only     | 'none'            | none — tool executes server-side (e.g. todo)|
 * | interactive O1  | 'out-of-band'     | existing /…/reply endpoints (Q / permission)|
 * | client-executed | 'client-executed' | AG-UI tool-result re-POST (db_editor)       |
 *
 * Pure data + a module-level Map ⇒ unit-testable, no React import here.
 */

/** How a registered tool's result is delivered back to the agent. */
export type AgentToolTransport =
  /** Server-executed; client only displays. Core stays an exact
   *  OpenCode mirror — zero behaviour change (Invariant 1). */
  | 'none'
  /** Bridge-wrapped HITL transport (papan_question / papan_permission).
   *  Suppressed as a normal tool-call block (its own prompt UI renders
   *  it); resolved out-of-band via the EXISTING unchanged
   *  /api/agent/{questions,permissions}/:id/reply endpoints. O1
   *  invariant — cascade-reject + always-allow stay in the core; do
   *  NOT move these to the AG-UI tool-result channel. */
  | 'out-of-band'
  /** A real model tool the *client* executes. Streams as a normal
   *  tool-call block; the runtime runs `handler` and re-POSTs an AG-UI
   *  tool-result message — the worker's blocked ctx.clientTool resolves
   *  and the result feeds the model loop. (db_editor — DN-I step 4.) */
  | 'client-executed'

export interface AgentToolHandlerCtx {
  /** The model's real tool-call id — the AG-UI tool-result must echo
   *  it so the worker's keyed wait (waitForClientToolResult) resolves. */
  toolCallId: string
  sessionId: string
}

/** Result a client-executed handler returns (mirrors the worker's
 *  ctx.clientTool result contract, serialized into the AG-UI
 *  tool-message `content`). */
export interface AgentToolResult {
  success: boolean
  data?: Record<string, unknown>
  error?: string
}

export interface AgentToolEntry {
  /** Resolution channel — the registry never decides this; the entry does. */
  transport: AgentToolTransport
  /**
   * Optional React component for this tool's chat block, fed STREAMING
   * args. Forward seam for the DN-I render-consolidation; this PR keeps
   * the proven ToolCallBlock/TodoPanel/QuestionPrompt path as the
   * default renderer (strangler: no working render code deleted until
   * its replacement is validated). Typed loosely to keep this module
   * React-free / unit-testable.
   */
  render?: (args: Record<string, unknown>, status: 'streaming' | 'running' | 'done') => unknown
  /**
   * Required for `transport: 'client-executed'`: execute the tool in
   * the browser and return its result. Optional/ignored for the other
   * flavors (render-only has none; out-of-band keeps the existing
   * QuestionPrompt/PermissionPrompt → reply-endpoint path untouched
   * per the O1 invariant).
   */
  handler?: (args: Record<string, unknown>, ctx: AgentToolHandlerCtx) => Promise<AgentToolResult>
  /**
   * Optional readiness gate for `transport: 'client-executed'`. When it
   * returns false the runtime does NOT consume the call or POST a result —
   * the next `/pending` poll retries until it returns true, so a handler
   * whose cross-feature surface (e.g. db_editor → DatabaseViewer) hasn't
   * re-mounted yet isn't a lost result. The race is removed (retry until the
   * precondition is effective, bounded by the worker's own ctx.clientTool
   * wait), not papered over with a client-side timeout.
   *
   * Consulted on every dispatch a RETRY WILL FOLLOW — the `/pending` replay
   * AND the worker's live `client-tool-request`, both of which the worker has
   * already recorded. NOT consulted on the model's own `tool-call-start`:
   * the worker may not have reached `ctx.clientTool` yet, so there may be no
   * record to retry from and declining would lose the call rather than defer
   * it. Those keep the handler's immediate, accurate fast-fail.
   *
   * Given the call's args, so a surface-bound tool can wait for ITS surface
   * (the one the worker stamped — #352), not for "any" surface.
   */
  ready?: (args?: Record<string, unknown>) => boolean
  /**
   * Cap, in ms from a wait's FIRST deferral, on how long `ready()` may keep
   * deferring it; after that the handler runs and answers (e.g. "no preview
   * open"). For tools whose surface can stay un-ready until the user acts
   * (preview_*: an uninstalled PDApp, a Settings face, a consent prompt).
   * Unset = no cap (db_editor: its mounting wait is deliberately uncapped).
   */
  maxDeferMs?: number
  /**
   * #352 I1 — can THIS page act on the call's surface right now? A session is
   * multi-attacher, so every tab showing it is offered the call and exactly one runs
   * it (a server claim). A page that can resolve the surface claims on its first
   * offer; one that can't waits until the wait was first seen ≥ 8 s ago, so a tab
   * with the surface wins and, when none has it, one answers the clear error. It
   * must ask exactly what the handler needs (a tab with no preview must not claim
   * first and answer "preview not open"). Absent = true (an id-bound tool: any tab
   * can act).
   */
  canResolve?: (args: Record<string, unknown>) => boolean
  /**
   * #352, owner ruling 2026-10-19 ("split by action") — is this call SIDE-EFFECT FREE,
   * so that after a page RELOAD its kept result may re-run once on the new page to
   * restore the UI it set (the editor showing the SQL again)? Judged on the dispatched
   * args when the result is kept. The replay runs only when the server re-offers the
   * call's wait after the reload (never from the pump alone), goes through `ready()`, takes no claim and
   * posts nothing (the kept result stays the answer). Absent = never replayed: anything
   * that writes, sends or reads live state the model already has (preview_eval,
   * preview_screenshot, the access tools) keeps "re-send only, never re-run". Decide on
   * what the handler EXECUTES, not on the action's name (db_editor: any truthy
   * `autoRun`, on set_sql and navigate alike, runs SQL — never replayable).
   */
  replayable?: (args: Record<string, unknown>) => boolean
  /**
   * #352 R-22 — the args a REPLAY binds to: `args` pinned to the exact surface the
   * handler is about to drive (db_editor: the stamped target with the fileId of the
   * editor `pickDbEditor` picks — with no chip, the workspace's ACTIVE tab, which the
   * user may switch before a reload). Asked synchronously right before the handler
   * runs (the handler picks the same way at its start). `undefined` = nothing to pin.
   * A tool that declares this never replays an entry kept WITHOUT a pin (an older
   * build's — it would bind to whatever editor is active now).
   */
  pinReplayTarget?: (args: Record<string, unknown>) => Record<string, unknown> | undefined
  /**
   * WHERE this tool's handler args come from — the fix for the shape split
   * that broke every access tool (#386 follow-up).
   *
   * `'tool-call'` (default) — the args streamed with the model's own
   *   tool-call (`tool-call-start.input`). Correct when the browser needs
   *   nothing the model didn't already say. (db_editor and preview_* are
   *   `'worker'`: the worker stamps the surface they are bound to — #352.)
   *
   * `'worker'` — the args the WORKER passed to `ctx.clientTool(name, args)`.
   *   Required when the worker NORMALIZES before dispatching — e.g.
   *   `share_link` resolves a `/papan/`-relative PATH to a concrete file id
   *   server-side, which the browser cannot redo. These arrive on the
   *   `client-tool-request` event (live) or the `/pending` poll (recovery);
   *   the runtime does NOT dispatch such a tool from `tool-call-start`,
   *   because the model's raw args are a DIFFERENT shape and running them
   *   would consume the callId and POST a wrong-args result.
   */
  argsFrom?: 'tool-call' | 'worker'
}

const registry = new Map<string, AgentToolEntry>()

/** Register (or replace) a client-side tool entry. Idempotent — safe to
 *  call at module load. NEVER advertises the tool to the model. */
export function registerAgentTool(name: string, entry: AgentToolEntry): void {
  registry.set(name, entry)
}

/** Lookup a registered entry (undefined ⇒ falls through to the default
 *  pipeline — unknown tools render as a normal block, unchanged). */
export function getAgentTool(name: string): AgentToolEntry | undefined {
  return registry.get(name)
}

/**
 * True for bridge-wrapped HITL transport tools that must NOT create a
 * normal tool-call UI block (their own prompt component renders them;
 * a phantom "Running… 0.3K" block would never close). Replaces the
 * hard-coded `name === 'papan_question' || …` lists in agui-to-stream.
 */
export function isSuppressedTransportTool(name: string): boolean {
  return getAgentTool(name)?.transport === 'out-of-band'
}

/** Every registered tool, for guard tests (#352 R2). */
export function listAgentTools(): Array<[name: string, entry: AgentToolEntry]> {
  return [...registry.entries()]
}

/** Test seam — clear the registry (registrations are module-load side
 *  effects; tests re-register what they need). */
export function __resetAgentToolRegistry(): void {
  registry.clear()
}
